//@ts-check
import { ObjectTree, NODE_TYPE } from '../../wgs/scene/ObjectTree';
import { InstanceTreeAccess } from '../../wgs/scene/InstanceTreeStorage';
import { RcsPropDbLoader, prop } from './RcsPropDbLoader.js';

/** @typedef {import('../../application/Model').Model} Model */
/** @typedef {import('../../application/EventDispatcher').EventDispatcher} EventDispatcher */
/** @typedef {import('./RcsPropDbLoader').RcsPropertyEntry} RcsPropertyEntry */
/** @typedef {import('./RcsPropDbLoader').RcsPropertyData} RcsPropertyData */
/** @typedef {import('../../wgs/scene/InstanceTreeStorage').InstanceTreeStorage} InstanceTreeStorage */

/**
 * DEBUGGING ONLY — do not use in production.
 *
 * Property database loader for RCS point clouds in debugOctree mode.
 * Creates a hierarchical tree: Top → [Data/Interior] → LOD Levels → Individual Nodes.
 *
 * WARNING: This code exists solely for debugging the RCS octree structure. It allocates
 * one instance-tree node per fragment (plus group nodes) and does not scale to large
 * point clouds. Production loads must use 'simple' mode.
 *
 * @class
 * @extends RcsPropDbLoader
 */
export class RcsDebugOctreePropDbLoader extends RcsPropDbLoader {

  /**
   * @param {Model} model
   * @param {EventDispatcher} eventTarget
   */
  constructor(model, eventTarget) {
    super(model, eventTarget);

    const n = this.numFragments;
    /** @type {number} - DbId of the top-level "RCS Point Cloud" root node */
    this.topLevelDbId = n;
    /** @type {number} - DbId of the "Point Cloud Data" group node */
    this.dataRootDbId = n + 1;
    /** @type {number} - DbId of the "Interior Nodes" group node */
    this.interiorRootDbId = n + 2;
    /** @type {number} - First DbId reserved for LOD / interior level grouping nodes */
    this.lodLevelDbIdStart = n + 3;
    this.rootDbId = this.topLevelDbId;
  }

  /**
   * @param {InstanceTreeStorage} storage
   * @returns {ObjectTree}
   * @protected
   */
  _buildInstanceTree(storage) {
    const topLevelDbId = this.topLevelDbId;
    const rootDbId = this.dataRootDbId;
    const interiorRootDbId = this.interiorRootDbId;
    const lodLevelDbIdStart = this.lodLevelDbIdStart;
    const numFragments = this.numFragments;

    // Separate regular fragments from interior nodes
    const regularFragments = [];
    const interiorFragments = [];
    const interiorLevelMap = new Map(); // level -> fragIds

    const bvh = this.model.getIterator().getBVH();

    for (let fragId = 0; fragId < numFragments; fragId++) {
      const nodeId = bvh.fragId2nodeId[fragId];
      const umbrellaIndex = this.rcs.getNodeUmbrellaIndex(nodeId);
      const lodLevel = this.rcs.getNodeLodLevel(nodeId);

      if (umbrellaIndex === -1 && lodLevel <= 0) {
        // Interior / HLOD inner node
        interiorFragments.push(fragId);
        const octreeLevel = this.rcs.getNodeOctreeLevel(nodeId);
        if (!interiorLevelMap.has(octreeLevel)) {
          interiorLevelMap.set(octreeLevel, []);
        }
        interiorLevelMap.get(octreeLevel).push(fragId);
      } else if (lodLevel >= 0) {
        regularFragments.push(fragId);
      }
    }

    // Group regular fragments by LOD level
    const lodToFragmentDbIds = new Map();
    for (const fragId of regularFragments) {
      const nodeId = bvh.fragId2nodeId[fragId];
      const lodLevel = this.rcs.getNodeLodLevel(nodeId);
      if (!lodToFragmentDbIds.has(lodLevel)) {
        lodToFragmentDbIds.set(lodLevel, []);
      }
      lodToFragmentDbIds.get(lodLevel).push(fragId);
    }

    const sortedLodLevels = Array.from(lodToFragmentDbIds.keys()).sort((a, b) => a - b);
    const sortedInteriorLevels = Array.from(interiorLevelMap.keys()).sort((a, b) => a - b);

    let totalNodeCount = 0;
    let currentDbId = lodLevelDbIdStart;

    // Top-level parent node
    const topChildren = [rootDbId];
    if (interiorFragments.length > 0) {
      topChildren.push(interiorRootDbId);
    }

    storage.setNode(
      topLevelDbId, // dbId
      0, // parentDbId (0 = root)
      "RCS Point Cloud", // name
      NODE_TYPE.NODE_TYPE_MODEL, // flags
      topChildren, // childrenIds
      [], // fragIds
      0 // level (depth 0)
    );
    totalNodeCount++;

    // "Point Cloud Data" node (regular nodes)
    const lodLevelDbIds = sortedLodLevels.map((lod) => currentDbId++);
    storage.setNode(
      rootDbId, // dbId
      topLevelDbId, // parentDbId
      "Point Cloud Data", // name
      NODE_TYPE.NODE_TYPE_ASSEMBLY, // flags
      lodLevelDbIds, // childrenIds (LOD level nodes)
      [], // fragIds
      1 // level (depth 1)
    );
    totalNodeCount++;

    // Create LOD level nodes for regular fragments
    for (let i = 0; i < sortedLodLevels.length; i++) {
      const lodLevel = sortedLodLevels[i];
      const lodDbId = lodLevelDbIds[i];
      const fragmentDbIds = lodToFragmentDbIds.get(lodLevel) || [];

      storage.setNode(
        lodDbId, // dbId
        rootDbId, // parentDbId
        `LOD ${lodLevel}`, // name
        NODE_TYPE.NODE_TYPE_ASSEMBLY, // flags
        fragmentDbIds, // childrenIds (fragment dbIds = fragIds)
        [], // fragIds
        2 // level (depth 2)
      );
      totalNodeCount++;

      // Create individual node for each fragment
      for (const fragId of fragmentDbIds) {
        const nodeId = bvh.fragId2nodeId[fragId];
        const umbrellaIndex = this.rcs.getNodeUmbrellaIndex(nodeId);
        const nodeName = `U${umbrellaIndex}_B${nodeId}`;

        storage.setNode(
          fragId, // dbId = fragId
          lodDbId, // parentDbId
          nodeName, // name
          NODE_TYPE.NODE_TYPE_ASSEMBLY, // flags
          [], // childrenIds
          [fragId], // fragIds
          3 // level (depth 3)
        );
        totalNodeCount++;
      }
    }

    // "Interior Nodes" hierarchy if any exist
    if (interiorFragments.length > 0) {
      const interiorLevelDbIds = sortedInteriorLevels.map(() => currentDbId++);

      storage.setNode(
        interiorRootDbId, // dbId
        topLevelDbId, // parentDbId
        "Interior Nodes", // name
        NODE_TYPE.NODE_TYPE_ASSEMBLY, // flags
        interiorLevelDbIds, // childrenIds (level nodes)
        [], // fragIds
        1 // level (depth 1)
      );
      totalNodeCount++;

      // Create level nodes for interior hierarchy
      for (let i = 0; i < sortedInteriorLevels.length; i++) {
        const level = sortedInteriorLevels[i];
        const levelDbId = interiorLevelDbIds[i];
        const fragmentDbIds = interiorLevelMap.get(level) || [];

        storage.setNode(
          levelDbId, // dbId
          interiorRootDbId, // parentDbId
          `Level ${level}`, // name
          NODE_TYPE.NODE_TYPE_ASSEMBLY, // flags
          fragmentDbIds, // childrenIds (fragment dbIds = fragIds)
          [], // fragIds
          2 // level (depth 2)
        );
        totalNodeCount++;

        // Create individual node for each interior fragment
        for (const fragId of fragmentDbIds) {
          const nodeName = `Interior_${fragId}`;

          storage.setNode(
            fragId, // dbId = fragId
            levelDbId, // parentDbId
            nodeName, // name
            NODE_TYPE.NODE_TYPE_ASSEMBLY, // flags
            [], // childrenIds
            [fragId], // fragIds
            3 // level (depth 3)
          );
          totalNodeCount++;
        }
      }
    }

    storage.flatten();
    const nodeAccess = new InstanceTreeAccess(storage, topLevelDbId, null);
    const maxDepth = 4; // top → data/interior → lod/level → nodes
    return new ObjectTree(nodeAccess, totalNodeCount, maxDepth);
  }

  /**
   * @param {number} dbId
   * @returns {RcsPropertyData|null}
   * @protected
   */
  _createPropertyData(dbId) {
    const numFragments = this.numFragments;
    const topLevelDbId = this.topLevelDbId;
    const rootDbId = this.dataRootDbId;
    const interiorRootDbId = this.interiorRootDbId;
    const lodLevelDbIdStart = this.lodLevelDbIdStart;

    if (dbId === topLevelDbId) {
      return {
        dbId: topLevelDbId,
        name: "RCS Point Cloud",
        externalId: "PointCloud_TopLevel",
        properties: [
        prop("Name", "RCS Point Cloud", "__name__"),
        prop("Type", "RCS Point Cloud Root")]

      };
    }
    if (dbId === rootDbId) {
      return {
        dbId: rootDbId,
        name: "Point Cloud Data",
        externalId: "PointCloud_Data",
        properties: [
        prop("Name", "Point Cloud Data", "__name__"),
        prop("Type", "Point Cloud Data Group")]

      };
    }
    if (dbId === interiorRootDbId) {
      return {
        dbId: interiorRootDbId,
        name: "Interior Nodes",
        externalId: "PointCloud_Interior",
        properties: [
        prop("Name", "Interior Nodes", "__name__"),
        prop("Type", "Interior Nodes Group")]

      };
    }
    if (lodLevelDbIdStart != null && dbId >= lodLevelDbIdStart) {
      // Could be a LOD level node or Interior level node
      // Try to get node name from instance tree
      const nodeName = this.instanceTree?.getNodeName(dbId, false);

      if (nodeName && (nodeName.startsWith('LOD ') || nodeName.startsWith('Level '))) {
        const isInteriorLevel = nodeName.startsWith('Level ');
        const levelNum = parseInt(nodeName.split(' ')[1]) || 0;

        return {
          dbId: dbId,
          name: nodeName,
          externalId: isInteriorLevel ? `PointCloud_InteriorLevel${levelNum}` : `PointCloud_LOD${levelNum}`,
          properties: [
          prop("Name", nodeName, "__name__"),
          prop("Type", isInteriorLevel ? "Interior Level" : "LOD Level"),
          prop(
            isInteriorLevel ? "Level" : "LOD Level",
            levelNum,
            "Item",
            3,
            isInteriorLevel ? "Level" : "LODLevel"
          )]

        };
      }
    }
    if (dbId >= 0 && dbId < numFragments) {
      // Individual fragment node: dbId = fragId (1:1 mapping)
      const fragId = dbId;
      const bvh = this.model.getIterator().getBVH();
      const bvhNodeIndex = bvh.fragId2nodeId[fragId];

      const umbrellaIndex = this.rcs.getNodeUmbrellaIndex(bvhNodeIndex);
      const lodLevel = this.rcs.getNodeLodLevel(bvhNodeIndex);
      const octreeLevel = this.rcs.getNodeOctreeLevel(bvhNodeIndex);
      const isInteriorNode = umbrellaIndex === -1;

      // Node naming: U{umbrella}_B{bvh} for regular nodes, Interior_{fragId} for interior nodes
      const nodeName = isInteriorNode ? `Interior_${fragId}` : `U${umbrellaIndex}_B${bvhNodeIndex}`;
      const nodeType = isInteriorNode ? "Interior Node (Hierarchical LOD)" : "Point Cloud Node";
      const externalId = isInteriorNode ? `PointCloud_Interior${fragId}` : `PointCloud_U${umbrellaIndex}_B${bvhNodeIndex}`;

      /** @type {RcsPropertyEntry[]} */
      const baseProperties = [
      prop("Name", nodeName, "__name__"),
      prop("Type", nodeType),
      prop("Fragment ID", fragId, "Item", 3, "FragmentID")];


      // Add type-specific properties
      if (isInteriorNode) {
        baseProperties.push(prop("BVH Level", octreeLevel, "Item", 3, "BVHLevel"));
      } else {
        baseProperties.push(
          prop("Umbrella Index", umbrellaIndex, "Item", 3, "UmbrellaIndex"),
          prop("LOD Level", lodLevel, "Item", 3, "LODLevel"),
          prop("BVH Node", bvhNodeIndex, "Item", 3, "BVHNode")
        );
      }

      return {
        dbId: dbId,
        name: nodeName,
        externalId: externalId,
        properties: baseProperties
      };
    }

    return null;
  }
}